import type { Dict } from "./fr";

export const en: Dict = {
  nav: {
    services: "Services",
    references: "References",
    about: "About",
    faq: "FAQ",
    tools: "Tools",
    contact: "Contact",
    cta: "Book a call",
  },
  common: {
    available: "Available for new mandates Q2 2026",
    learnMore: "Explore this service",
    discoverServices: "Discover our services",
    bookCall: "Book a call",
    bookSlot: "Book a slot",
    talk: "Discuss your needs",
    seeAllRefs: "See all references",
    moreAboutAlex: "More about Alexandre",
    followLinkedIn: "Follow Alexandre on LinkedIn",
    sendEmail: "SEND\nAN EMAIL",
    call: "Call",
    send: "Send",
  },
  breadcrumb: {
    home: "Home",
  },
  home: {
    title: "Hotel consultant · Opening & management | 2AM Hospitality",
    description:
      "Independent hotel consultant in Belgium & Luxembourg: opening, pre-opening, management, short-stay and hybrid hospitality.",
    heroTitlePre: "Hospitality, ",
    heroTitleAccent: "structured.",
    heroTitlePost: "\nFrom pre-opening to operational efficiency.",
    heroSub:
      "Independent hospitality consulting firm based in Brussels. We support independent hotels, boutique hotels and hybrid concepts across Belgium and Luxembourg — from pre-opening to daily optimisation.",
    seoLead:
      "Independent hotel consultant — opening, pre-opening, management and operations. Short-stay operator and hybrid hospitality across Belgium and Luxembourg.",
    stats: [
      { v: "15+", l: "years of experience" },
      { v: "40+", l: "properties supported" },
      { v: "2", l: "countries (Belgium & Luxembourg)" },
      { v: "100%", l: "independent" },
    ],
    statsSubtext: [
      "in operational hotel management",
      "from 15 to 450 rooms, BE & LU",
      "Belgium & Luxembourg",
      "since 2022",
    ],
    offersTitle: "Three pillars of support",
    offersSub: "A complete spectrum to address your challenges, from one-off to operational.",
    offers: [
      {
        title: "Operations management",
        desc: "Day-to-day operations, pre-opening and opening. On-site or remote leadership, standards, team structuring and process design.",
        tags: ["Pre-opening", "Opening", "Operations"],
      },
      {
        title: "Targeted consulting",
        desc: "Flash audits and focused engagements to improve your operations: diagnosis, concrete action plan, measured results.",
        tags: ["Audit", "Action plan", "ROI"],
      },
      {
        title: "Permits, IT & automation",
        desc: "Operating permits, hotel IT infrastructure and process automation. The technical expertise that frees up your teams.",
        tags: ["Permits", "Hotel IT", "Automation"],
      },
    ],
    methodTitle: "A clear method, measurable results.",
    method: [
      { t: "Listen", d: "We understand your context, constraints and goals." },
      { t: "Diagnose", d: "Operational, financial and technical audit. Quantified, factual." },
      { t: "Act", d: "Prioritised action plan. On-site execution or team coaching." },
      { t: "Measure", d: "Clear KPIs, regular reporting, continuous adjustments." },
    ],
    forWhoTitle: "Who do we work with?",
    forWho: [
      "Independent hotels",
      "Boutique hotels",
      "Small hotel groups in structuring",
      "Short-stay and short-stay operations",
      "Hybrid hospitality (aparthotel, coliving-hotel)",
      "From economy to experiential",
    ],
    refsTitle: "Trusted by",
    aboutTeaserTitle: "10 years on the floor. Not in the boardroom.",
    aboutTeaser:
      "We have structured, opened and turned around hotels for 10 years. With 2AM Hospitality, he puts that expertise to work for operators who want concrete, measurable, independent results.",
    finalTitle: "A project, a question, an audit?",
    finalSub: "​",
    manifestoTitle: ["When the lobby ", "goes quiet,", "we're awake."],
    manifestoDesc:
      "2AM is the hour the night auditor closes the books and the guests are asleep. \nIt’s also the hour where every operational decision can be taken — in revenue, in guest experience, in margin.",
    readMore: "Read the full story",
  },
  services: {
    title: "Hotel consultant services — Belgium & Luxembourg",
    description:
      "Hotel opening & pre-opening, management, short-stay and hybrid hospitality. Permits and hotel IT in Belgium & Luxembourg.",
    h1: "Hotel consultant & operator: opening, pre-opening, management and operations",
    sub: "Three pillars, one method, measurable results.",
    tldr: "In one sentence: 2AM Hospitality is a Belgian hotel consulting and operating firm specialised in opening, pre-opening, management and operations of hotels — including short-stay and hybrid hospitality in Belgium and Luxembourg.",
    tldrLabel: "In one sentence",
    modality: "Modalities",
    modalityValue: "Fixed-fee, time & materials, or monthly retainer.",
    forWho: "For whom?",
    concretely: "What we do concretely",
    whyTitle: "Why 2AM?",
    why: [
      { e: "🎯", t: "Field-first", d: "We have operated hotels, not just advised on them." },
      { e: "📊", t: "Quantified & deliverable", d: "No deck — concrete KPIs and actions." },
      { e: "⚙️", t: "Dual culture", d: "Operations + tech + permits under one roof." },
      { e: "📍", t: "Rooted in Belgium", d: "Knowledge of the market, regulations and local players." },
    ],
    tableTitle: "Compare the three offers",
    tableHeaders: ["", "Operations management", "Targeted consulting", "Permits & IT"],
    tableRows: [
      { l: "Typical duration", v: ["3 to 12 months", "2 to 30 days", "1 to 6 months"] },
      { l: "Format", v: ["Fixed-fee or retainer", "Fixed-fee", "Fixed-fee or T&M"] },
      { l: "Deliverables", v: ["KPI reporting, SOPs, standards", "Prioritised action plan", "Permit file, configured IT stack"] },
      { l: "Best for", v: ["Pre-opening, takeover, continuous improvement", "Flash diagnosis, turnaround", "New openings, IT redesign"] },
    ],
    miniFaqTitle: "Frequently asked about our services",
    miniFaq: [
      {
        q: "What's the difference between a flash audit and operational support?",
        a: "A flash audit (2-5 days) delivers a quantified diagnosis and action plan. Operational support is the execution of that plan over several months, on site or remotely.",
      },
      {
        q: "Can I combine several services?",
        a: "Yes. Most engagements combine an initial audit (targeted consulting) with execution support (operations management). We adapt the scope after a first scoping call.",
      },
      {
        q: "Do you only work in Belgium?",
        a: "Belgium and Luxembourg are our core markets. We accept punctual engagements across the rest of Europe based on availability.",
      },
    ],
    faqLinkTitle: "Other questions?",
    faqLinkText: "Pricing, timelines, modalities: read our full FAQ.",
    faqLinkCta: "Read the FAQ",
    shortStayTitle: "Short-stay operator & consultant · hybrid hospitality",
    shortStayDesc:
      "Beyond the classic hotel, we support short-stay operators (short stays, aparthotels) and hybrid hospitality concepts (coliving-hotel, aparthotel-coworking). Operations structuring, Belgian regulatory compliance, PMS stack tailored to longer stays and automation of the guest journey without 24/7 reception.",
    shortStayBullets: [
      "Short-stay operator: processes, lean teams, multi-channel distribution",
      "Hybrid hospitality: aparthotel, coliving-hotel, hotel-coworking models",
      "Short-stay specific compliance (Brussels, Wallonia, Flanders, Luxembourg)",
      "Automation: online check-in, smart locks, digital concierge",
    ],
    items: [
      {
        id: "operations",
        num: "01",
        title: "Operations management",
        intro:
          "From pre-opening to daily operational leadership. On-site presence or remote management, depending on your needs.",
        tldr:
          "In one sentence: we run pre-opening and operational management for independent hotels, boutique hotels and small groups in Belgium & Luxembourg, on-site or remote, with SOPs, KPI reporting and team leadership.",
        bullets: [
          "Pre-opening support: planning, recruitment, training, procurement",
          "Opening leadership (soft opening + grand opening)",
          "Interim operational management",
          "Standards and SOPs design",
          "Daily operational reporting setup",
          "Team and shift management",
          "Coordination with partners (laundry, F&B, maintenance)",
        ],
        tags: ["Independent hotels", "Boutique hotels", "Small groups"],
      },
      {
        id: "consulting",
        num: "02",
        title: "Targeted consulting on operations improvement",
        intro: "A short, focused, quantified engagement. On-site diagnosis, prioritised action plan, KPIs.",
        tldr:
          "In one sentence: a 2 to 5-day on-site flash audit with financial diagnosis, mystery visit and a quantified prioritised action plan to recover or optimise an operating hotel, starting at €4,500 ex. VAT.",
        bullets: [
          "Flash operational audit (2-5 days on site)",
          "Financial diagnosis and benchmarking",
          "Front-office and housekeeping process review",
          "Prioritised action plan with KPIs",
          "Mystery visit and guest journey analysis",
          "Distribution and revenue management recommendations",
          "Monthly execution follow-up (optional)",
        ],
        tags: ["Operating hotels", "Groups in structuring"],
      },
      {
        id: "tech",
        num: "03",
        title: "Targeted consulting: permits, IT & automation",
        intro:
          "The technical expertise that frees teams: regulatory compliance, modern IT stack, workflow automation.",
        tldr:
          "In one sentence: we file BE/LU operating permits, select and integrate the PMS stack (Mews, Cloudbeds, Misterbooking, Hotellab) and automate check-in, smart locks and digital concierge for openings and hybrid concepts.",
        bullets: [
          "Operating permits application and renewal",
          "Regulatory compliance control",
          "Hotel IT infrastructure audit and redesign (PMS, channel manager, wifi, TV, smart locks)",
          "Selection and integration of the right tech stack (PMS & global IT suite)",
          "Automation: online check-in, upsell, digital concierge, accounting connectors",
        ],
        tags: ["Pre-opening", "Hotels in transformation", "Hybrid concepts"],
      },
    ],
  },
  references: {
    title: "Hotel consultant references — 2AM Hospitality",
    description:
      "Hotel consultant engagements in Belgium: openings, operations, short-stay and hybrid hospitality. Boutique hotels and aparthotels.",
    h1: "Engagements: hotel opening, pre-opening, management and operations",
    sub: "Recent engagements",
    typesTitle: "Types of properties supported",
    types: [
      "Independent hotels",
      "Boutique hotels",
      "Economy groups",
      "Short-stay & aparthotels",
      "Hybrid concepts",
    ],
    items: [
      {
        type: "Boutique hotel 4★ · 45 rooms · Brussels",
        mission: "Pre-opening support",
        duration: "6 months · 2024-2025",
        results: [
          "Full team recruitment and training",
          "PMS and distribution stack setup",
          "On-time opening with TripAdvisor 4.7 from month one",
        ],
        tag: "Operations",
      },
      {
        type: "3-hotel group · Luxembourg",
        mission: "Operational audit & action plan",
        duration: "3 months · 2024",
        results: [
          "On-site diagnosis across 3 properties",
          "Prioritised action plan on 12 levers",
          "+11 pts gross operating margin in 6 months",
        ],
        tag: "Consulting",
      },
      {
        type: "Hybrid aparthotel · Belgium",
        mission: "IT redesign & automation",
        duration: "4 months · 2025",
        results: [
          "Migration to modern cloud PMS stack",
          "100% online check-in + smart locks",
          "−40% admin time per stay",
        ],
        tag: "Tech",
      },
    ],
    cta: "Want to discuss a project?",
  },
  about: {
    title: "About · Alexandre Dufrene — 2AM Hospitality",
    description:
      "Alexandre Dufrene, founder of 2AM Hospitality. 15 years of hotel operations (B&B HOTELS, ACCOR) at the service of operators.",
    h1: "15 years on the floor. Not in the boardroom.",
    sub: "Alexandre Dufrene — hotel consultant, short-stay operator and hybrid hospitality expert in Belgium & Luxembourg.",
    bio: "After several years leading hotel openings and takeovers for B&B HOTELS and independent groups, Alexandre Dufrene founded 2AM Hospitality in 2022 with one conviction: independent hoteliers deserve support as rigorous as that of the major groups, without the bureaucracy. \n\nHis dual culture — operations on the ground and portfolio development — lets him speak fluently with reception teams, investors and owners alike. \n\n10 years on the floor. Measurable results.",
    tldr: "In one sentence: Alexandre Dufrene, founder of 2AM Hospitality, is a hotel consultant and short-stay & hybrid hospitality operator, ex-B&B HOTELS and Accor, expert in PMS Mews, Cloudbeds and Misterbooking across Belgium & Luxembourg.",
    servicesCtaTitle: "Want to see how we work?",
    servicesCta: "Discover our services",
    journeyTitle: "Journey",
    journey: [
      { y: "2022", t: "2AM Hospitality founded" },
      { y: "2019-2022", t: "Multi-site operations · B&B HOTELS" },
      { y: "2009-2019", t: "Hotel management · independent groups" },
    ],
    whyTitle: "Why 2AM?",
    why: "2AM is the hour when a hotel lives unfiltered, where everyone is sleeping but everything is at stake. \n\nIt's through anticipation that operational quality is truly measured.",
    valuesTitle: "Values",
    values: [
      { t: "Concrete", d: "On-site, quantified, deliverable. No empty slides." },
      { t: "Smart", d: "Task automation to free up value-added time for teams" },
      { t: "Measurable", d: "KPIs defined before the engagement, measured after." },
    ],
  },
  faq: {
    title: "Hotel consultant FAQ — Belgium & Luxembourg",
    description: "Frequently asked questions about hospitality consulting in Belgium and Luxembourg.",
    h1: "FAQ — hotel consultant, opening & operations",
    glossaryTitle: "Glossary — key terms",
    glossary: [
      { term: "Hybrid hospitality", def: "A concept blending hotel, aparthotel, coliving or coworking under one roof. Reduced reception, short or long stays, fully digital guest journey." },
      { term: "Short-stay", def: "Operating furnished accommodations for short stays (1 to 30 nights), as a complement or alternative to traditional hotels. Subject to specific regulations in Brussels, Wallonia, Flanders and Luxembourg." },
      { term: "Pre-opening", def: "The 6 to 12 month phase before a hotel's commercial opening: hiring, training, procurement, PMS setup, soft opening." },
      { term: "PMS (Property Management System)", def: "Core hotel operations software (reservations, check-in, billing, reporting). Examples: Mews, Cloudbeds, Misterbooking, Apaleo, Opera Cloud." },
    ],
    items: [
      {
        q: "What is your service area?",
        a: "Belgium and Luxembourg primarily, with occasional travel across Europe. Based in Incourt, we frequently work in Brussels, Antwerp, Ghent, Liège and Luxembourg City.",
      },
      {
        q: "How much does a typical engagement cost?",
        a: "A flash audit starts at €4,500 (2 to 5 days). Pre-opening support ranges from €25,000 to €80,000 depending on scope. Monthly optimisation retainer: €2,500 to €6,000/month. Tailored quote after scoping.",
      },
      {
        q: "Do you work fixed-fee or time & materials?",
        a: "Both. Fixed-fee for scoped engagements (audits, openings), time & materials for interim management or evolving needs. Monthly retainer is preferred for continuous follow-up.",
      },
      {
        q: "Do you offer interim management?",
        a: "Yes, hotel general management or multi-site operational direction, on durations of 1 to 6 months.",
      },
      {
        q: "What is the minimum engagement length?",
        a: "2 days for a flash audit. No maximum.",
      },
      {
        q: "How does a pre-opening engagement unfold?",
        a: "Initial scoping, detailed D-180 to D+30 plan, recruitment, training, process setup, soft opening, grand opening, then structured handover to in-place teams.",
      },
      {
        q: "Do you work with independents or only groups?",
        a: "Both. Our core target: independents, boutique hotels and small groups in structuring. From economy to experiential.",
      },
      {
        q: "Do you have expertise on operating permits in Belgium and Luxembourg?",
        a: "Yes: operating permits, fire safety, AFSCA, urbanism, municipal declarations. We work with specialised lawyers for complex cases.",
      },
      {
        q: "Which PMS do you master?",
        a: "Mews, Cloudbeds, Hotellab, Apaleo, Opera Cloud, Misterbooking, plus channel managers (SiteMinder, D-EDGE, Cubilis) and revenue management tools.",
      },
      {
        q: "How do we start working together?",
        a: "A 30-minute discovery call (free, no commitment) via Calendly, or an email to hello@2amhospitality.com. We come back within 48h with a scope and proposal.",
      },
      {
        q: "Do you support short-stay and hybrid hospitality projects?",
        a: "Yes. We work with short-stay operators (short stays, aparthotels) and hybrid hospitality concepts (coliving-hotel, aparthotel-coworking): operations structuring, Belgian and Luxembourg regulatory compliance, tailored PMS stack, guest journey automation (online check-in, smart locks).",
      },
      {
        q: "What is the difference between a hotel consultant and a hotel operator?",
        a: "A hotel consultant advises, audits and structures; a hotel operator runs the property day-to-day. 2AM Hospitality combines both: we operated hotels for 10 years before consulting — leading to recommendations directly applicable on the floor and the option to take interim operational leadership.",
      },
    ],
  },
  contact: {
    title: "Contact 2AM Hospitality — hotel consultant",
    description: "Contact 2AM Hospitality: book a call, email, phone. Brussels · Belgium · Luxembourg.",
    h1: "Let's talk.",
    sub: "Let's discuss your project: hotel opening, management, short-stay operations or hybrid hospitality.",
    bookTitle: "Book a call",
    bookDesc: "Reserve a 30-min slot directly.",
    emailTitle: "Email",
    phoneTitle: "Phone",
    formTitle: "Or send us a message",
    faqLinkText: "Have questions before reaching out?",
    faqLinkCta: "Read the FAQ",
    responseGuarantee: "Reply guaranteed within 24 working hours",
    fields: {
      name: "Full name",
      email: "Email",
      company: "Company / project",
      type: "Request type",
      typeOptions: ["Operations", "Targeted consulting", "Tech & Permits", "Other"],
      message: "Message",
      gdpr: "I agree my data is used to respond to my request.",
    },
    success: "Thanks, your message has been sent.",
  },
  footer: {
    pitch: "Hospitality consulting firm. Belgium & Luxembourg. Independent, on the ground, measurable.",
    nav: "Navigation",
    services: "Services",
    contact: "Contact",
    legal: "Legal notice",
    privacy: "Privacy policy",
    cookies: "Cookies",
    rights: "All rights reserved.",
  },
  related: {
    eyebrow: "Explore further",
    title: "Go deeper",
    cta: "Discover",
    cards: {
      services: {
        title: "Hotel consultant services",
        desc: "Opening, management, operations and hybrid hospitality.",
      },
      references: {
        title: "Recent engagements",
        desc: "Real cases: openings, audits, operations, short-stay.",
      },
      about: {
        title: "The founder",
        desc: "Alexandre Dufrene — 10 years of hotel operations in Belgium.",
      },
      faq: {
        title: "FAQ",
        desc: "Pricing, timelines, scope, supported PMS: all the answers.",
      },
      tools: {
        title: "Hotel tools",
        desc: "Free calculators and resources for hotel operators.",
      },
      contact: {
        title: "Contact & booking",
        desc: "30-min discovery call, email or phone — reply within 24h.",
      },
    },
  },
  cookies: {
    banner: {
      title: "Your cookie preferences",
      body: "We use cookies to keep the site running and, with your consent, to measure how it is used. You can accept, reject or customize at any time.",
      learnMore: "Read our cookie policy",
      acceptAll: "Accept all",
      rejectAll: "Reject all",
      customize: "Customize",
    },
    prefs: {
      title: "Cookie preferences",
      subtitle: "Choose the categories you allow.",
      save: "Save my choices",
      close: "Close",
    },
    cats: {
      necessary: {
        title: "Strictly necessary",
        desc: "Required for the site to work (security, language preference, navigation). Always on.",
      },
      analytics: {
        title: "Audience measurement",
        desc: "Anonymised statistics (Google Analytics) to understand site usage and improve it.",
      },
      marketing: {
        title: "Marketing",
        desc: "Advertising or social-network cookies. None are active today — reserved for future use.",
      },
    },
    page: {
      eyebrow: "Cookie policy",
      title: "Cookie policy",
      intro:
        "This page explains which cookies and similar technologies are used on 2amhospitality.com, and how you can manage your preferences at any time.",
      manageTitle: "Manage my preferences",
      manageBody: "You can change your choices at any time. This will reopen the preferences window.",
      manageCta: "Update my preferences",
      sections: [
        {
          title: "What is a cookie?",
          body: "A cookie is a small text file placed on your device when you visit a website. It can remember your language, secure a session, or measure how the site is used.",
        },
        {
          title: "Strictly necessary cookies",
          body: "These cookies are essential to the site: language preference, security, load balancing. They do not require consent and cannot be disabled.",
        },
        {
          title: "Audience measurement cookies",
          body: "With your consent, we use Google Analytics to measure traffic in an anonymised way (anonymised IP, no cross-service tracking). This helps us improve content and usability.",
        },
        {
          title: "Marketing cookies",
          body: "No advertising cookies are set today. This category is reserved for future use (e.g. a LinkedIn pixel) and will remain off until you explicitly allow it.",
        },
        {
          title: "Storage duration",
          body: "Your consent choice is stored in your browser for 12 months. After that, the consent window will appear again.",
        },
        {
          title: "Your rights",
          body: "Under the GDPR, you have the right to access, rectify, erase and object to your data. To exercise these rights, contact hello@2amhospitality.com.",
        },
      ],
    },
  },
  tools: {
    title: "Hotel tools — 2AM Hospitality",
    description:
      "Discover the tools and platforms developed or supported by 2AM Hospitality: Onerly, Maintel.io, Groupsmadeeasy.",
    eyebrow: "Tools",
    h1: "Tools by a hotel consultant & operator",
    sub: "Platforms we develop or support for hotel opening, hotel management, short-stay and hybrid hospitality.",
    statusLive: "Live",
    statusWip: "In development",
    visit: "Visit website",
    wipNote: "Available soon",
    items: [
      {
        name: "Onerly",
        tagline: "​",
        description:
          "A platform showcasing the hospitality projects and independent concepts we support.",
        status: "live",
        url: "#",
      },
      {
        name: "Maintel.io",
        tagline: "​",
        description:
          "Technical and maintenance management solution designed for demanding hospitality operations.",
        status: "live",
        url: "#",
      },
      {
        name: "Groupsmadeeasy",
        tagline: "Currently in development",
        description:
          "Group reservations management tool for hoteliers, currently being designed. Launch coming soon.",
        status: "wip",
        url: "#",
      },
    ],
  },
  legal: {
    page: {
      eyebrow: "Legal notice",
      title: "Legal notice",
      intro:
        "Legal information about the publisher, hosting and use of the 2amhospitality.com website.",
      sections: [
        {
          title: "Publisher",
          body: "2AM Hospitality\nFounder: Alexandre Dufrene\nRue du Pachy 6, 1315 Incourt, Belgium\nEmail: hello@2amhospitality.com\nPhone: +32 479 89 52 15",
        },
        {
          title: "Publication director",
          body: "Alexandre Dufrene, founder of 2AM Hospitality.",
        },
        {
          title: "Hosting",
          body: "The website is hosted on edge cloud infrastructure (Cloudflare Workers). Application data is hosted on Supabase infrastructure (European Union).",
        },
        {
          title: "Intellectual property",
          body: "All content on this site (text, visuals, logos, brands, structure) is the exclusive property of 2AM Hospitality unless otherwise stated. Any reproduction, representation, modification or distribution, in whole or in part, without prior written permission, is prohibited.",
        },
        {
          title: "Liability",
          body: "2AM Hospitality strives to ensure the accuracy and timeliness of the information published on this site but cannot guarantee its completeness. Users acknowledge using this information at their own risk.",
        },
        {
          title: "External links",
          body: "The site may contain links to third-party websites. 2AM Hospitality has no control over these sites and disclaims any responsibility for their content.",
        },
        {
          title: "Applicable law",
          body: "These legal notices are governed by Belgian law. Any dispute relating to the site falls under the exclusive jurisdiction of the courts of Brussels.",
        },
        {
          title: "Contact",
          body: "For any question relating to the site or this legal notice: hello@2amhospitality.com",
        },
      ],
    },
  },
  privacy: {
    page: {
      eyebrow: "Privacy",
      title: "Privacy policy",
      intro:
        "This policy describes how 2AM Hospitality collects, uses and protects your personal data, in accordance with the General Data Protection Regulation (GDPR).",
      sections: [
        {
          title: "Data controller",
          body: "2AM Hospitality\nRue du Pachy 6, 1315 Incourt, Belgium\nContact: hello@2amhospitality.com",
        },
        {
          title: "Data collected",
          body: "We collect the data you provide via the contact form (name, email, company, type of request, message) as well as anonymised technical browsing data (with your consent, via Google Analytics).",
        },
        {
          title: "Purposes of processing",
          body: "Your data is used to: respond to your requests, organise a meeting or call, manage our commercial relationship, measure site traffic (anonymised statistics) and comply with our legal obligations.",
        },
        {
          title: "Legal basis",
          body: "Processing is based on your consent (form, analytics cookies), pre-contractual measures at your request, and our legitimate interest in developing our business.",
        },
        {
          title: "Retention period",
          body: "Messages received via the form are kept for 3 years from the last contact. Analytics data is retained for a maximum of 14 months. Cookie consent is stored for 12 months.",
        },
        {
          title: "Recipients",
          body: "Your data is only accessible to 2AM Hospitality. It may be processed by our technical sub-processors (Supabase hosting, Resend email delivery, Google Analytics), all contractually bound to comply with GDPR.",
        },
        {
          title: "Transfers outside the EU",
          body: "Some sub-processors (Google Analytics) may involve transfers outside the European Union, governed by the European Commission's standard contractual clauses.",
        },
        {
          title: "Your rights",
          body: "Under the GDPR, you have the following rights: access, rectification, erasure, restriction, objection, portability, and withdrawal of consent at any time. To exercise these rights: hello@2amhospitality.com.",
        },
        {
          title: "Complaints",
          body: "You may lodge a complaint with the Belgian Data Protection Authority (APD): www.autoriteprotectiondonnees.be",
        },
        {
          title: "Security",
          body: "We implement appropriate technical and organisational measures (TLS encryption, access control, backups) to protect your data against any unauthorised access, alteration or destruction.",
        },
      ],
    },
  },
  geo: "We operate in Brussels, Antwerp, Ghent, Liège, Namur, Charleroi, Bruges, Leuven, Luxembourg City and Esch-sur-Alzette — across Belgium and the Grand Duchy.",
};
